<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">

      <!-- Setup checklist (shown until all steps complete) -->
      <div v-if="!setupComplete" class="setup-banner">
        <div class="setup-header">
          <span class="setup-title">GETTING STARTED</span>
          <span class="setup-sub">Complete these steps to activate your project</span>
        </div>
        <div class="setup-steps">
          <div class="setup-step" :class="{ done: setup_status.has_ai_clients }">
            <span class="step-check">{{ setup_status.has_ai_clients ? '✓' : '1' }}</span>
            <div class="step-body">
              <span class="step-label">Assign an AI client</span>
              <span class="step-desc">Connect Claude, GPT-4 or Gemini to this project</span>
            </div>
            <button v-if="!setup_status.has_ai_clients" class="step-btn" @click="goTo('settings')">SETTINGS →</button>
          </div>
          <div class="setup-step" :class="{ done: setup_status.has_pipeline }">
            <span class="step-check">{{ setup_status.has_pipeline ? '✓' : '2' }}</span>
            <div class="step-body">
              <span class="step-label">Bootstrap the pipeline</span>
              <span class="step-desc">AI generates an initial multi-step prompt pipeline</span>
            </div>
            <button v-if="!setup_status.has_pipeline" class="step-btn" @click="goTo('pipeline')">PIPELINE →</button>
          </div>
          <div class="setup-step" :class="{ done: setup_status.has_api_key }">
            <span class="step-check">{{ setup_status.has_api_key ? '✓' : '3' }}</span>
            <div class="step-body">
              <span class="step-label">Create an API key</span>
              <span class="step-desc">Required for production callers (optional for playground)</span>
            </div>
            <button v-if="!setup_status.has_api_key" class="step-btn" @click="goTo('settings')">SETTINGS →</button>
          </div>
          <div class="setup-step" :class="{ done: setup_status.has_messages }">
            <span class="step-check">{{ setup_status.has_messages ? '✓' : '4' }}</span>
            <div class="step-body">
              <span class="step-label">Run your first test</span>
              <span class="step-desc">Use the playground to verify the pipeline works</span>
            </div>
            <button v-if="!setup_status.has_messages" class="step-btn" @click="goTo('test')">PLAYGROUND →</button>
          </div>
        </div>
      </div>

      <!-- Stat row -->
      <div class="stat-row">
        <StatCard
          label="RAG Knowledge"
          :value="stats.rag_count"
          sub="documents stored"
          trend="neutral"
        />
        <StatCard
          label="Eval Score"
          :value="evalScoreDisplay"
          :sub="evalScoreSub"
          :trend="evalTrend"
        />
        <StatCard
          label="Chats"
          :value="stats.chat_count"
          sub="total sessions"
          trend="neutral"
        />
        <StatCard
          label="Pipeline Steps"
          :value="stats.pipeline_steps"
          :sub="stats.pipeline_steps > 0 ? 'active pipeline' : 'no active pipeline'"
          :trend="stats.pipeline_steps > 0 ? 'positive' : 'warning'"
        />
      </div>

      <!-- Quick actions -->
      <div class="quick-actions">
        <button class="qa-btn qa-primary" @click="goTo('test')">
          <span class="qa-icon">▶</span> RUN IN PLAYGROUND
        </button>
        <button class="qa-btn" :disabled="!setup_status.has_pipeline || triggeringEval" @click="triggerEval">
          <span class="qa-icon">◈</span>
          <span>{{ triggeringEval ? 'QUEUED...' : 'TRIGGER EVAL' }}</span>
        </button>
        <button class="qa-btn" :disabled="!setup_status.has_pipeline || triggeringOrch" @click="triggerOrch">
          <span class="qa-icon">⟳</span>
          <span>{{ triggeringOrch ? 'QUEUED...' : 'RUN ORCHESTRATOR' }}</span>
        </button>
      </div>

      <!-- Main content grid -->
      <div class="main-grid">

        <!-- Recent messages with feedback -->
        <Panel class="messages-panel">
          <template #header>
            <span class="panel-title">RECENT MESSAGES</span>
            <span class="panel-meta">{{ recent_messages.length }} messages</span>
          </template>
          <div v-if="recent_messages.length === 0" class="empty-row">
            No messages yet — run a test in the playground.
          </div>
          <div v-for="msg in messageState" :key="msg.id" class="msg-row">
            <div class="msg-meta-row">
              <span class="msg-time">{{ formatDate(msg.created_at) }}</span>
              <span class="msg-cost">${{ msg.cost.toFixed(4) }}</span>
              <span class="msg-tokens">{{ msg.tokens_in + msg.tokens_out }} tokens</span>
            </div>
            <div class="msg-io">
              <div class="msg-io-block">
                <span class="io-label">IN</span>
                <span class="io-text">{{ truncate(jsonStr(msg.input), 120) }}</span>
              </div>
              <div class="msg-io-block">
                <span class="io-label out">OUT</span>
                <span class="io-text">{{ truncate(jsonStr(msg.output), 120) }}</span>
              </div>
            </div>
            <div class="msg-feedback-row">
              <div v-if="msg.feedback_type" class="feedback-badge" :class="msg.feedback_type">
                {{ feedbackLabel(msg.feedback_type) }}
              </div>
              <template v-else>
                <button
                  class="fb-btn fb-up"
                  :disabled="msg.submitting"
                  @click="submitFeedback(msg, 'thumbs_up')"
                >👍</button>
                <button
                  class="fb-btn fb-down"
                  :disabled="msg.submitting"
                  @click="submitFeedback(msg, 'thumbs_down')"
                >👎</button>
                <button
                  class="fb-btn fb-correct"
                  :disabled="msg.submitting"
                  @click="openCorrection(msg)"
                >✎ CORRECT</button>
              </template>
            </div>
            <!-- Correction editor -->
            <div v-if="msg.correcting" class="correction-editor">
              <textarea
                v-model="msg.correctionText"
                class="correction-ta"
                placeholder="Paste correct JSON output..."
              />
              <div class="correction-actions">
                <button class="corr-cancel" @click="msg.correcting = false">Cancel</button>
                <button
                  class="corr-submit"
                  :disabled="!msg.correctionText || msg.submitting"
                  @click="submitFeedback(msg, 'correction')"
                >SUBMIT CORRECTION</button>
              </div>
            </div>
          </div>
        </Panel>

        <!-- Right column: evals + orchestrator -->
        <div class="right-col">
          <!-- Recent evals -->
          <Panel>
            <template #header>
              <span class="panel-title">RECENT EVAL RUNS</span>
              <span class="panel-meta">{{ recent_evals.length }} runs</span>
            </template>
            <div v-if="recent_evals.length === 0" class="empty-row">
              No eval runs yet.
            </div>
            <div v-for="run in recent_evals" :key="run.id" class="list-row">
              <div class="row-left">
                <span class="dot" :class="statusColor(run.status)" />
                <span class="row-label">{{ run.triggered_by }}</span>
              </div>
              <ScoreBar :score="run.score" />
              <span class="row-meta">{{ formatDate(run.created_at) }}</span>
            </div>
          </Panel>

          <!-- Orchestrator runs -->
          <Panel>
            <template #header>
              <span class="panel-title">ORCHESTRATOR RUNS</span>
              <span class="panel-meta">{{ recent_runs.length }} runs</span>
            </template>
            <div v-if="recent_runs.length === 0" class="empty-row">
              No orchestrator runs yet.
            </div>
            <div v-for="run in recent_runs" :key="run.id" class="list-row">
              <div class="row-left">
                <span class="dot" :class="statusColor(run.status)" />
                <span class="row-label">{{ run.triggered_by }}</span>
              </div>
              <div class="improvement-indicator">
                <span class="score-chip before">{{ run.score_before.toFixed(1) }}%</span>
                <span class="arrow">→</span>
                <span class="score-chip after" :class="{ improved: run.was_improvement }">
                  {{ run.score_after > 0 ? run.score_after.toFixed(1) + '%' : '...' }}
                </span>
              </div>
              <span class="row-meta">{{ formatDate(run.created_at) }}</span>
            </div>
          </Panel>
        </div>

      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from "vue"
import { router } from "@inertiajs/vue3"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import StatCard from "../../components/StatCard.vue"
import Panel from "../../components/Panel.vue"
import ScoreBar from "../../components/ScoreBar.vue"

const props = defineProps({
  project:         { type: Object, required: true },
  stats:           { type: Object, required: true },
  recent_evals:    { type: Array,  default: () => [] },
  recent_runs:     { type: Array,  default: () => [] },
  setup_status:    { type: Object, default: () => ({ has_ai_clients: false, has_pipeline: false, has_api_key: false, has_messages: false }) },
  recent_messages: { type: Array,  default: () => [] },
})

const setupComplete = computed(() =>
  props.setup_status.has_ai_clients &&
  props.setup_status.has_pipeline &&
  props.setup_status.has_api_key &&
  props.setup_status.has_messages
)

// Reactive message state (for inline feedback UI)
const messageState = reactive(
  props.recent_messages.map(m => ({
    ...m,
    submitting:     false,
    correcting:     false,
    correctionText: "",
  }))
)

// Quick action state
const triggeringEval = ref(false)
const triggeringOrch = ref(false)

const evalScoreDisplay = computed(() => `${props.stats.eval_score.toFixed(1)}%`)
const evalScoreSub     = computed(() => props.stats.eval_score > 0 ? "accuracy" : "no eval data")
const evalTrend        = computed(() => {
  if (props.stats.eval_score >= 90) return "positive"
  if (props.stats.eval_score >= 80) return "neutral"
  if (props.stats.eval_score > 0)   return "negative"
  return "warning"
})

function goTo(tab) {
  router.visit(`/projects/${props.project.uuid}/${tab}`)
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content ?? ""
}

async function triggerEval() {
  triggeringEval.value = true
  try {
    await fetch(`/api/v1/projects/${props.project.uuid}/eval`, {
      method:  "POST",
      headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
    })
  } finally {
    setTimeout(() => { triggeringEval.value = false }, 2000)
  }
}

async function triggerOrch() {
  triggeringOrch.value = true
  try {
    await fetch(`/api/v1/projects/${props.project.uuid}/orchestrate`, {
      method:  "POST",
      headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
    })
  } finally {
    setTimeout(() => { triggeringOrch.value = false }, 2000)
  }
}

function openCorrection(msg) {
  msg.correcting     = true
  msg.correctionText = ""
}

async function submitFeedback(msg, type) {
  msg.submitting = true
  if (type !== "correction") msg.correcting = false

  const body = { type }
  if (type === "correction") body.correct_answer = msg.correctionText

  try {
    const res = await fetch(`/api/v1/projects/${props.project.uuid}/messages/${msg.id}/feedback`, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify(body),
    })
    if (res.ok) {
      msg.feedback_type = type
      msg.correcting    = false
    }
  } finally {
    msg.submitting = false
  }
}

function feedbackLabel(type) {
  return { thumbs_up: "👍 CORRECT", thumbs_down: "👎 WRONG", correction: "✎ CORRECTED" }[type] ?? type
}

function statusColor(status) {
  const map = { complete: "green", failed: "red", running: "yellow", queued: "yellow", analyzing: "yellow" }
  return map[status] ?? "dim"
}

function formatDate(dateStr) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
}

function jsonStr(val) {
  if (!val) return ""
  if (typeof val === "string") return val
  return JSON.stringify(val)
}

function truncate(str, len) {
  if (!str) return ""
  return str.length > len ? str.slice(0, len) + "…" : str
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: #0a0a0b;
  position: relative;
}

.bg-grid {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
}

/* Setup banner */
.setup-banner {
  background: rgba(245, 166, 35, 0.05);
  border: 1px solid rgba(245, 166, 35, 0.2);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.setup-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 16px;
}

.setup-title {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #f5a623;
}

.setup-sub {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.4);
}

.setup-steps {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.setup-step {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  transition: border-color 0.2s;
}

.setup-step.done {
  border-color: rgba(74, 222, 128, 0.25);
  background: rgba(74, 222, 128, 0.04);
}

.step-check {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 166, 35, 0.15);
  color: #f5a623;
  flex-shrink: 0;
}

.setup-step.done .step-check {
  background: rgba(74, 222, 128, 0.2);
  color: #4ade80;
}

.step-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
}

.step-label {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  color: #e8e6e3;
}

.step-desc {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.35);
  line-height: 1.4;
}

.step-btn {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #f5a623;
  background: transparent;
  border: 1px solid rgba(245, 166, 35, 0.3);
  border-radius: 3px;
  padding: 4px 8px;
  cursor: pointer;
  transition: all 0.15s;
  align-self: flex-start;
}

.step-btn:hover {
  background: rgba(245, 166, 35, 0.1);
  border-color: #f5a623;
}

/* Stat row */
.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

/* Quick actions */
.quick-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.qa-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  padding: 10px 18px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(232, 230, 227, 0.7);
  cursor: pointer;
  transition: all 0.15s;
}

.qa-btn:hover:not(:disabled) {
  border-color: rgba(245, 166, 35, 0.4);
  color: #f5a623;
  background: rgba(245, 166, 35, 0.06);
}

.qa-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.qa-btn.qa-primary {
  border-color: rgba(245, 166, 35, 0.5);
  color: #f5a623;
  background: rgba(245, 166, 35, 0.08);
}

.qa-btn.qa-primary:hover {
  background: rgba(245, 166, 35, 0.14);
  border-color: #f5a623;
}

.qa-icon {
  font-size: 13px;
}

/* Main grid */
.main-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 16px;
  align-items: start;
}

.right-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Panel labels */
.panel-title {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(232, 230, 227, 0.45);
}

.panel-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

/* Empty state */
.empty-row {
  padding: 24px 18px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: rgba(232, 230, 227, 0.25);
  text-align: center;
}

/* Message rows */
.msg-row {
  padding: 14px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.msg-meta-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.msg-time {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.3);
}

.msg-cost,
.msg-tokens {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
}

.msg-io {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.msg-io-block {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.io-label {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: rgba(245, 166, 35, 0.7);
  background: rgba(245, 166, 35, 0.1);
  border-radius: 2px;
  padding: 2px 5px;
  flex-shrink: 0;
  margin-top: 1px;
}

.io-label.out {
  color: rgba(74, 222, 128, 0.7);
  background: rgba(74, 222, 128, 0.1);
}

.io-text {
  font-family: var(--font-mono);
  font-size: 11px;
  color: rgba(232, 230, 227, 0.6);
  line-height: 1.4;
  word-break: break-all;
}

.msg-feedback-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.feedback-badge {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 3px;
}

.feedback-badge.thumbs_up {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
}

.feedback-badge.thumbs_down {
  background: rgba(248, 113, 113, 0.15);
  color: #f87171;
}

.feedback-badge.correction {
  background: rgba(245, 166, 35, 0.15);
  color: #f5a623;
}

.fb-btn {
  font-family: var(--font-mono);
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(232, 230, 227, 0.5);
  cursor: pointer;
  transition: all 0.15s;
}

.fb-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: #e8e6e3;
}

.fb-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.fb-btn.fb-up:hover:not(:disabled) {
  border-color: rgba(74, 222, 128, 0.4);
  color: #4ade80;
}

.fb-btn.fb-down:hover:not(:disabled) {
  border-color: rgba(248, 113, 113, 0.4);
  color: #f87171;
}

.fb-btn.fb-correct {
  font-size: 10px;
  letter-spacing: 0.5px;
}

.fb-btn.fb-correct:hover:not(:disabled) {
  border-color: rgba(245, 166, 35, 0.4);
  color: #f5a623;
}

/* Correction editor */
.correction-editor {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.correction-ta {
  width: 100%;
  min-height: 80px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(245, 166, 35, 0.25);
  border-radius: 4px;
  color: #e8e6e3;
  font-family: var(--font-mono);
  font-size: 11px;
  padding: 10px;
  resize: vertical;
  box-sizing: border-box;
}

.correction-ta:focus {
  outline: none;
  border-color: rgba(245, 166, 35, 0.5);
}

.correction-actions {
  display: flex;
  gap: 8px;
}

.corr-cancel {
  font-family: var(--font-mono);
  font-size: 10px;
  padding: 5px 12px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: rgba(232, 230, 227, 0.4);
  cursor: pointer;
}

.corr-cancel:hover {
  color: rgba(232, 230, 227, 0.7);
}

.corr-submit {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 5px 14px;
  border-radius: 3px;
  border: 1px solid rgba(245, 166, 35, 0.4);
  background: rgba(245, 166, 35, 0.1);
  color: #f5a623;
  cursor: pointer;
  transition: all 0.15s;
}

.corr-submit:hover:not(:disabled) {
  background: rgba(245, 166, 35, 0.2);
}

.corr-submit:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Eval/Orchestrator rows */
.list-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  transition: background 0.15s;
}

.list-row:hover {
  background: rgba(245, 166, 35, 0.04);
}

.row-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot.green  { background: #4ade80; box-shadow: 0 0 6px rgba(74, 222, 128, 0.6); }
.dot.yellow { background: #fbbf24; box-shadow: 0 0 6px rgba(251, 191, 36, 0.6); }
.dot.red    { background: #f87171; box-shadow: 0 0 6px rgba(248, 113, 113, 0.6); }
.dot.dim    { background: rgba(255, 255, 255, 0.2); }

.row-label {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #e8e6e3;
  text-transform: capitalize;
}

.row-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: rgba(232, 230, 227, 0.25);
  white-space: nowrap;
}

.improvement-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
}

.score-chip {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 3px;
}

.score-chip.before {
  background: rgba(248, 113, 113, 0.15);
  color: #f87171;
}

.score-chip.after {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.score-chip.after.improved {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
}

.arrow {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #f5a623;
}
</style>
